import { Box } from '@mui/material';
import { JSX, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE, ICON_CLOSE, ICON_DELETE_REMOVE, ICON_MOVE_OR_SCROLL_DOWN, ICON_MOVE_OR_SCROLL_UP } from 'Config/Icons';
import { TPRM_ASSESSMENT_CONFIGURATION_REQUIRED_CHECKBOX, TPRM_ASSESSMENT_CONFIGURATION_UNACCEPTABLE_CHECKBOX, UCRM_ASSESSMENT_CONFIGURATION_REQUIRED_CHECKBOX, UCRM_ASSESSMENT_CONFIGURATION_UNACCEPTABLE_CHECKBOX } from 'Config/Tooltips';
import { DueDiligenceQuestionConfiguration, DueDiligenceQuestionType, DueDiligenceSelectableAnswerOption } from 'Models/DueDiligence';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating } from 'Models/RiskRating';

import styles from './Modal.module.css';

export interface QuestionModalProps {
    existingTexts: string[];
    hideModal: () => void;
    module: DueDiligenceModule;
    onSave: (question: DueDiligenceQuestionConfiguration) => void;

    /** If provided, the modal is in edit mode with this question pre-populated. */
    question?: DueDiligenceQuestionConfiguration;
}

interface OptionState {
    id: string;
    isUnacceptable: boolean;
    text: string;
}

const QUESTION_TYPE_OPTIONS = [
    { value: DueDiligenceQuestionType.SINGLE_SELECT, label: 'Single-select' },
    { value: DueDiligenceQuestionType.MULTIPLE_SELECT, label: 'Multiple-select' },
    { value: DueDiligenceQuestionType.FREEFORM_TEXT, label: 'Freeform text' },
    { value: DueDiligenceQuestionType.FILE_UPLOAD, label: 'File upload' },
    { value: DueDiligenceQuestionType.DATE, label: 'Date' },
];

const ALL_RISK_RATINGS = [RiskRating.LOW, RiskRating.LOW_MODERATE, RiskRating.MODERATE, RiskRating.MODERATE_HIGH, RiskRating.HIGH];

const getOptionsFromQuestion = (question: DueDiligenceQuestionConfiguration | undefined): OptionState[] => {
    if (question === undefined) {
        return [];
    }

    return (() => {
        switch (question._type) {
            case DueDiligenceQuestionType.DATE:
            case DueDiligenceQuestionType.FILE_UPLOAD:
            case DueDiligenceQuestionType.FREEFORM_TEXT:
                return [];
            case DueDiligenceQuestionType.MULTIPLE_SELECT:
            case DueDiligenceQuestionType.SINGLE_SELECT:
                // Why `uuidv4`? Options cannot use their text as a unique identifier, or else the text field loses focus as soon as the user starts typing to rename an option. A random ID works here for the `key` to use when `map`ping over options.
                return question.options.map((o) => ({ id: uuidv4(), text: o.text, isUnacceptable: o.is_unacceptable }));
        }
    })();
};

/**
 * Modal for adding or editing a question.
 * Handles all question types with appropriate fields for each type.
 */
export const QuestionModal = (props: QuestionModalProps): JSX.Element => {
    const { existingTexts, hideModal, onSave, question } = props;
    const isEditMode = !!question;

    const [questionType, setQuestionType] = useState<DueDiligenceQuestionType>(question?._type ?? DueDiligenceQuestionType.SINGLE_SELECT);
    const [text, setText] = useState(question?.text ?? '');
    const [options, setOptions] = useState<OptionState[]>(getOptionsFromQuestion(question));
    const [isRequired, setIsRequired] = useState<boolean>(question?.is_required ?? false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const hasUnacceptableOption = options.some((o) => o.isUnacceptable);

    const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setText(event.target.value);
    };

    const handleOptionTextChange = (index: number, newText: string): void => {
        setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, text: newText } : o)));
    };

    const handleOptionUnacceptableChange = (index: number, isUnacceptable: boolean): void => {
        setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, isUnacceptable } : o)));
    };

    const handleAddOption = (): void => {
        // Why `uuidv4`? Options cannot use their text as a unique identifier, or else the text field loses focus as soon as the user starts typing to rename an option. A random ID works here for the `key` to use when `map`ping over options.
        setOptions((prev) => [...prev, { id: uuidv4(), text: '', isUnacceptable: false }]);
    };

    const handleRemoveOption = (index: number): void => {
        setOptions((prev) => prev.filter((_, i) => i !== index));
    };

    const handleMoveOptionUp = (index: number): void => {
        setOptions((prev) => {
            const newOptions = [...prev];
            [newOptions[index - 1], newOptions[index]] = [newOptions[index], newOptions[index - 1]];
            return newOptions;
        });
    };

    const handleMoveOptionDown = (index: number): void => {
        setOptions((prev) => {
            const newOptions = [...prev];
            [newOptions[index + 1], newOptions[index]] = [newOptions[index], newOptions[index + 1]];
            return newOptions;
        });
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        setErrorMessage(undefined);

        const trimmedText = text.trim();

        if (trimmedText.length === 0) {
            setErrorMessage('Question text is required.');
            return;
        }

        if (existingTexts.includes(trimmedText)) {
            setErrorMessage('A question with this text already exists in this control.');
            return;
        }

        if (questionType === DueDiligenceQuestionType.SINGLE_SELECT || questionType === DueDiligenceQuestionType.MULTIPLE_SELECT) {
            const validOptions = options.filter((o) => o.text.trim().length > 0);
            if (validOptions.length < 1) {
                setErrorMessage('At least one answer option is required for this question type.');
                return;
            }

            const trimmedOptionTexts = validOptions.map((o) => o.text.trim());
            const uniqueTexts = new Set(trimmedOptionTexts);
            if (uniqueTexts.size !== trimmedOptionTexts.length) {
                setErrorMessage('Answer options must be unique.');
                return;
            }
        }

        const baseQuestion = {
            mapped_risk_ratings: question?.mapped_risk_ratings ?? ALL_RISK_RATINGS,
            text: trimmedText,
            is_required: isRequired || hasUnacceptableOption,
        };

        let newQuestion: DueDiligenceQuestionConfiguration;

        if (questionType === DueDiligenceQuestionType.SINGLE_SELECT) {
            newQuestion = {
                ...baseQuestion,
                _type: DueDiligenceQuestionType.SINGLE_SELECT,
                options: options.filter((o) => o.text.trim().length > 0).map((o): DueDiligenceSelectableAnswerOption => ({ is_unacceptable: o.isUnacceptable, text: o.text.trim() })),
            };
        } else if (questionType === DueDiligenceQuestionType.MULTIPLE_SELECT) {
            newQuestion = {
                ...baseQuestion,
                _type: DueDiligenceQuestionType.MULTIPLE_SELECT,
                options: options.filter((o) => o.text.trim().length > 0).map((o): DueDiligenceSelectableAnswerOption => ({ is_unacceptable: o.isUnacceptable, text: o.text.trim() })),
            };
        } else if (questionType === DueDiligenceQuestionType.FREEFORM_TEXT) {
            newQuestion = {
                ...baseQuestion,
                _type: DueDiligenceQuestionType.FREEFORM_TEXT,
            };
        } else if (questionType === DueDiligenceQuestionType.FILE_UPLOAD) {
            newQuestion = {
                ...baseQuestion,
                _type: DueDiligenceQuestionType.FILE_UPLOAD,
            };
        } else {
            newQuestion = {
                ...baseQuestion,
                _type: DueDiligenceQuestionType.DATE,
            };
        }

        onSave(newQuestion);
    };

    return (
        <Modal open header={isEditMode ? 'Edit Question' : 'Add Question'}>
            {errorMessage && <Alert type="error">{errorMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <div className={styles.formField}>
                    <FormFieldSelect formFieldId="questionType" formFieldLabel="Question Type" options={QUESTION_TYPE_OPTIONS} selectedOption={questionType} handleChange={(value) => setQuestionType(value as DueDiligenceQuestionType)} required />
                </div>

                <div className={styles.formField}>
                    <FormFieldTextArea formFieldId="text" formFieldLabel="Question Text" value={text} handleChange={handleTextChange} required />
                </div>

                <div className={styles.formField}>
                    <Checkbox label="Required" checked={isRequired || hasUnacceptableOption} disabled={hasUnacceptableOption} onToggled={setIsRequired} tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_CONFIGURATION_REQUIRED_CHECKBOX : TPRM_ASSESSMENT_CONFIGURATION_REQUIRED_CHECKBOX} />
                </div>

                {(questionType === DueDiligenceQuestionType.SINGLE_SELECT || questionType === DueDiligenceQuestionType.MULTIPLE_SELECT) && (
                    <div className={styles.formField}>
                        <Text variant="Text3">Answer Options</Text>
                        {options.length > 0 && (
                            <div className={styles.optionsContainer}>
                                {options.map((option, index) => (
                                    <div key={option.id} className={styles.optionRow}>
                                        <IconButton disabled={index === 0} aria-label={`Move up ${option.text}`} onClick={() => handleMoveOptionUp(index)} fontAwesomeImage={ICON_MOVE_OR_SCROLL_UP} />
                                        <IconButton disabled={index === options.length - 1} aria-label={`Move down ${option.text}`} onClick={() => handleMoveOptionDown(index)} fontAwesomeImage={ICON_MOVE_OR_SCROLL_DOWN} />
                                        <div className={styles.optionInput}>
                                            <FormFieldText formFieldId={`option-${index}`} placeholder={`Option ${index + 1}`} value={option.text} handleChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOptionTextChange(index, e.target.value)} />
                                        </div>
                                        <div className={styles.unacceptableCheckbox}>
                                            <Checkbox label="Unacceptable" checked={option.isUnacceptable} onToggled={(isChecked) => handleOptionUnacceptableChange(index, isChecked)} tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_CONFIGURATION_UNACCEPTABLE_CHECKBOX : TPRM_ASSESSMENT_CONFIGURATION_UNACCEPTABLE_CHECKBOX} />
                                        </div>
                                        <IconButton aria-label={`Remove ${option.text}`} onClick={() => handleRemoveOption(index)} fontAwesomeImage={ICON_DELETE_REMOVE} />
                                    </div>
                                ))}
                            </div>
                        )}
                        <Button variant="secondary" onClick={handleAddOption} fontAwesomeImage={ICON_ADD_CREATE}>
                            Add Option
                        </Button>
                    </div>
                )}

                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Cancel
                    </Button>
                    <Button variant="submit">Save</Button>
                </div>
            </Box>
        </Modal>
    );
};
